import { FC, useState, useEffect, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { Check, AlertTriangle } from 'lucide-react';
import { Input } from 'components/forms/fragments/Input';
import { InputWithUnit } from 'components/forms/fragments/InputWithUnit';
import { Button } from 'components/ui/Button';
import { MessagesTooltip } from 'components/feedback/MessagesTooltip';
import { InfoIcon } from 'components/icons/Icons';
import {
  PossibleAnswer,
  PossibleAnswerWithLabel,
  InputAnswer,
  InputWithUnitAnswer,
  InputValidationRule,
  HierarchicalAnswer
} from 'features/subscription-process/profiling-process/types/interfaces';
import {
  isIconAnswer,
  isLabelAnswer,
  isBooleanKeyAnswer,
  isBooleanAnswer,
  isNumberAnswer,
  isStringAnswer,
  isInputAnswer,
  isInputWithUnitAnswer,
  isHierarchicalAnswer
} from 'features/subscription-process/profiling-process/types/possibleAnswer';
import { QuickGroupSurveyQuestionProps } from './quickGroupSurvey.types';
import { QuickGroupSurveyDropdown } from './QuickGroupSurveyDropdown';
import { GenericHierarchicalQuestion, useIsHierarchicalQuestion } from 'features/subscription-process/profiling-process/components/generic/GenericHierarchicalQuestion';

const DEFAULT_DROPDOWN_THRESHOLD = 4;

export const QuickGroupSurveyQuestion: FC<QuickGroupSurveyQuestionProps> = ({
  question,
  currentAnswers = [],
  onAnswer,
  dropdownThreshold = DEFAULT_DROPDOWN_THRESHOLD,
  warningMessage,
}) => {
  // État local pour les inputs
  const getDefaultValue = (): string => {
    if (currentAnswers[0] !== undefined && typeof currentAnswers[0] === 'string') {
      return currentAnswers[0];
    }
    const inputAnswer = question.possibleAnswers.find(
      (a): a is InputAnswer | InputWithUnitAnswer =>
        isInputAnswer(a) || isInputWithUnitAnswer(a)
    );
    return inputAnswer?.config?.defaultValue || '';
  };

  const [inputValue, setInputValue] = useState<string>(getDefaultValue);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [hasBeenTouched, setHasBeenTouched] = useState(false);

  // Sync avec currentAnswers si elles changent de l'extérieur
  useEffect(() => {
    if (currentAnswers[0] !== undefined && typeof currentAnswers[0] === 'string') {
      setInputValue(currentAnswers[0]);
    }
  }, [currentAnswers]);

  // Fonction de validation générique
  const validateInput = useCallback((value: string, rules?: InputValidationRule[]): string[] => {
    if (!rules || rules.length === 0) return [];

    const errors: string[] = [];

    for (const rule of rules) {
      switch (rule.type) {
        case 'required':
          if (!value || value.trim() === '') {
            errors.push(rule.message);
          }
          break;
        case 'min':
          if (value && Number(value) < Number(rule.value)) {
            errors.push(rule.message);
          }
          break;
        case 'max':
          if (value && Number(value) > Number(rule.value)) {
            errors.push(rule.message);
          }
          break;
        case 'minLength':
          if (value && value.length < Number(rule.value)) {
            errors.push(rule.message);
          }
          break;
        case 'maxLength':
          if (value && value.length > Number(rule.value)) {
            errors.push(rule.message);
          }
          break;
        case 'pattern':
          if (value && !new RegExp(rule.value as string).test(value)) {
            errors.push(rule.message);
          }
          break;
        case 'email':
          if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            errors.push(rule.message);
          }
          break;
        case 'dateAfterToday': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate <= today) {
              errors.push(rule.message);
            }
          }
          break;
        }
        case 'dateBeforeToday': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate >= today) {
              errors.push(rule.message);
            }
          }
          break;
        }
        case 'dateWithinMonths': {
          if (value) {
            const inputDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const maxDate = new Date(today);
            maxDate.setMonth(maxDate.getMonth() + Number(rule.value));
            inputDate.setHours(0, 0, 0, 0);
            if (inputDate > maxDate) {
              errors.push(rule.message);
            }
          }
          break;
        }
        case 'custom':
          if (rule.validate && !rule.validate(value)) {
            errors.push(rule.message);
          }
          break;
      }
    }

    return errors;
  }, []);

  const handleInputChange = useCallback((value: string, rules?: InputValidationRule[]) => {
    setInputValue(value);
    setHasBeenTouched(true);
    const errors = validateInput(value, rules);
    setValidationErrors(errors);
  }, [validateInput]);

  const handleInputValidation = useCallback((rules?: InputValidationRule[]) => {
    const errors = validateInput(inputValue, rules);
    setValidationErrors(errors);
    setHasBeenTouched(true);

    if (errors.length === 0 && inputValue) {
      onAnswer(inputValue as any);
    }
  }, [inputValue, validateInput, onAnswer]);

  const isInputValid = useCallback((rules?: InputValidationRule[]): boolean => {
    if (!inputValue) return false;
    const errors = validateInput(inputValue, rules);
    return errors.length === 0;
  }, [inputValue, validateInput]);

  // Vérifier si cette question est hiérarchique
  const isHierarchical = useIsHierarchicalQuestion(question);

  // Déterminer le type de réponses
  const answerTypes = useMemo(() => {
    const types = {
      hasIcons: false,
      hasLabels: false,
      hasBooleanKeys: false,
      hasBoolean: question.type === 'boolean',
      hasNumbers: false,
      hasStrings: false,
      hasInputs: false,
      hasInputsWithUnit: false,
    };

    question.possibleAnswers.forEach(answer => {
      if (isIconAnswer(answer)) types.hasIcons = true;
      else if (isBooleanKeyAnswer(answer)) types.hasBooleanKeys = true;
      else if (isHierarchicalAnswer(answer)) { /* handled separately */ }
      else if (isLabelAnswer(answer)) types.hasLabels = true;
      else if (isBooleanAnswer(answer)) types.hasBoolean = true;
      else if (isNumberAnswer(answer)) types.hasNumbers = true;
      else if (isStringAnswer(answer)) types.hasStrings = true;
      else if (isInputAnswer(answer)) types.hasInputs = true;
      else if (isInputWithUnitAnswer(answer)) types.hasInputsWithUnit = true;
    });

    return types;
  }, [question]);

  // Fonction pour déterminer si une réponse est sélectionnée
  const isAnswerSelected = useCallback((answer: PossibleAnswer): boolean => {
    if (!currentAnswers || currentAnswers.length === 0) return false;

    if (isIconAnswer(answer)) {
      return currentAnswers.some(current =>
        isIconAnswer(current) && current.key === answer.key
      );
    }
    if (isBooleanKeyAnswer(answer)) {
      return currentAnswers.some(current =>
        isBooleanKeyAnswer(current) && current.key === answer.key
      );
    }
    if (isLabelAnswer(answer)) {
      return currentAnswers.some(current =>
        isLabelAnswer(current) && current.key === answer.key
      );
    }
    if (isHierarchicalAnswer(answer)) {
      return currentAnswers.some(current =>
        isHierarchicalAnswer(current) && (current as any).key === (answer as any).key
      );
    }
    if (isBooleanAnswer(answer) || isNumberAnswer(answer) || isStringAnswer(answer)) {
      return currentAnswers.includes(answer);
    }
    return false;
  }, [currentAnswers]);

  // Si c'est une question hiérarchique
  if (isHierarchical) {
    const currentAnswer = (currentAnswers as HierarchicalAnswer[])[0];
    return (
      <div className="px-4 py-3 flex items-center justify-between gap-4">
        <label className="text-sm text-slate-600 min-w-0 flex-shrink-0">
          {question.label}
        </label>
        <div className="flex-1 max-w-xs">
          <GenericHierarchicalQuestion
            question={question}
            currentAnswerKey={currentAnswer?.key}
            onAnswer={(key, answers) => {
              if (answers[0]) {
                onAnswer(answers[0] as PossibleAnswerWithLabel);
              }
            }}
          />
        </div>
      </div>
    );
  }

  // Rendu des réponses avec icônes - style moderne flat
  const renderIconAnswers = () => {
    const iconAnswers = question.possibleAnswers.filter(isIconAnswer);

    // Utiliser dropdown si plus de 2 réponses
    if (iconAnswers.length > 2) {
      const selectedKeys = currentAnswers
        .filter(isIconAnswer)
        .map(a => String(a.key));

      const dropdownAnswers = iconAnswers.map(a => ({
        key: String(a.key),
        label: a.label,
        iconKey: a.iconKey
      }));

      return (
        <QuickGroupSurveyDropdown
          answers={dropdownAnswers}
          selectedKeys={selectedKeys}
          onSelect={(selected) => {
            const originalAnswer = iconAnswers.find(a => String(a.key) === selected.key);
            if (originalAnswer) onAnswer(originalAnswer);
          }}
          multiple={question.isMultipleChoice}
        />
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {iconAnswers.map((answer, index) => {
          const selected = isAnswerSelected(answer);
          return (
            <button
              key={`${question.key}-${index}`}
              type="button"
              onClick={() => onAnswer(answer)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
                'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                selected
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              {selected && <Check className="w-4 h-4" />}
              <span>{answer.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu des réponses booléennes - style moderne flat
  const renderBooleanAnswers = () => (
    <div className="flex gap-2">
      {[
        { value: true, label: 'Oui' },
        { value: false, label: 'Non' }
      ].map(({ value, label }) => {
        const selected = isAnswerSelected(value);
        return (
          <button
            key={`${question.key}-${value}`}
            type="button"
            onClick={() => onAnswer(value as any)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-all duration-150',
              'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
              selected
                ? 'border-primary bg-primary text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
  );

  // Rendu des réponses BooleanKey - style moderne flat
  const renderBooleanKeyAnswers = () => {
    const booleanKeyAnswers = question.possibleAnswers.filter(isBooleanKeyAnswer);
    return (
      <div className="flex gap-2">
        {booleanKeyAnswers.map((answer, index) => {
          const selected = isAnswerSelected(answer.key);
          return (
            <button
              key={`${question.key}-${index}`}
              type="button"
              onClick={() => onAnswer(answer.key as any)}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-150',
                'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                selected
                  ? 'border-primary bg-primary text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              {answer.label}
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu des réponses numériques - style moderne flat
  const renderNumberAnswers = () => {
    const numberAnswers = question.possibleAnswers.filter(isNumberAnswer);
    return (
      <div className="flex flex-wrap gap-2">
        {numberAnswers.map((answer, index) => {
          const selected = isAnswerSelected(answer);
          return (
            <button
              key={`${question.key}-${index}`}
              type="button"
              onClick={() => onAnswer(answer as any)}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-150',
                'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                selected
                  ? 'border-primary bg-primary text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              {answer}
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu des réponses textuelles - style moderne flat
  const renderStringAnswers = () => {
    const stringAnswers = question.possibleAnswers.filter(isStringAnswer);
    return (
      <div className="flex flex-wrap gap-2">
        {stringAnswers.map((answer, index) => {
          const selected = isAnswerSelected(answer);
          return (
            <button
              key={`${question.key}-${index}`}
              type="button"
              onClick={() => onAnswer(answer as any)}
              className={clsx(
                'px-4 py-2 rounded-md text-sm font-medium transition-all duration-150',
                'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                selected
                  ? 'border-primary bg-primary text-white'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              {answer}
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu des champs de saisie - style moderne flat
  const renderInputAnswer = () => {
    const inputAnswer = question.possibleAnswers.find(isInputAnswer) as InputAnswer | undefined;
    if (!inputAnswer) return null;

    const rules = inputAnswer.validationRules;
    const isValid = isInputValid(rules);
    const showErrors = hasBeenTouched && validationErrors.length > 0;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <Input
            value={inputValue}
            type={inputAnswer.config.type}
            onChange={(e) => handleInputChange(e.target.value, rules)}
            className={clsx(
              'flex-1 max-w-xs text-sm',
              showErrors && 'border-red-400 focus:border-red-400 focus:ring-red-400/30'
            )}
          />
          <Button
            color="secondary"
            onClick={() => handleInputValidation(rules)}
            disabled={!inputValue || !isValid}
            className="text-sm px-3 py-2"
          >
            Valider
          </Button>
        </div>
        {showErrors && validationErrors.map((error, i) => (
          <p key={i} className="text-xs text-red-500">{error}</p>
        ))}
      </div>
    );
  };

  // Rendu des champs de saisie avec unité - style moderne flat
  const renderInputWithUnitAnswer = () => {
    const inputAnswer = question.possibleAnswers.find(isInputWithUnitAnswer) as InputWithUnitAnswer | undefined;
    if (!inputAnswer) return null;

    const rules = inputAnswer.validationRules;
    const isValid = isInputValid(rules);
    const showErrors = hasBeenTouched && validationErrors.length > 0;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex gap-2 items-center">
          <InputWithUnit
            value={inputValue}
            type={inputAnswer.config.type}
            unit={inputAnswer.config.unit}
            min={inputAnswer.config.min}
            max={inputAnswer.config.max}
            placeholder={inputAnswer.config.placeholder}
            onChange={(e) => handleInputChange(e.target.value, rules)}
            className={clsx(
              'flex-1 max-w-xs text-sm',
              showErrors && 'border-red-400 focus:border-red-400 focus:ring-red-400/30'
            )}
          />
          <Button
            color="secondary"
            onClick={() => handleInputValidation(rules)}
            disabled={!inputValue || !isValid}
            className="text-sm px-3 py-2"
          >
            Valider
          </Button>
        </div>
        {showErrors && validationErrors.map((error, i) => (
          <p key={i} className="text-xs text-red-500">{error}</p>
        ))}
      </div>
    );
  };

  // Rendu des réponses avec label (avec dropdown si nécessaire) - style moderne flat
  const renderLabelAnswers = () => {
    const labelAnswers = question.possibleAnswers.filter(
      (answer): answer is PossibleAnswerWithLabel =>
        typeof answer === 'object' && answer !== null && 'label' in answer && 'key' in answer &&
        !isIconAnswer(answer) && !isBooleanKeyAnswer(answer) && !isHierarchicalAnswer(answer)
    );

    if (labelAnswers.length === 0) return null;

    const selectedKeys = currentAnswers.filter(isLabelAnswer).map(a => a.key);

    // Utiliser dropdown si plus de 2 réponses ou choix multiple
    if (question.isMultipleChoice || labelAnswers.length > 2) {
      return (
        <QuickGroupSurveyDropdown
          answers={labelAnswers}
          selectedKeys={selectedKeys}
          onSelect={onAnswer}
          multiple={question.isMultipleChoice}
        />
      );
    }

    return (
      <div className="flex flex-wrap gap-2">
        {labelAnswers.map((answer) => {
          const selected = isAnswerSelected(answer);
          return (
            <button
              key={String(answer.key)}
              type="button"
              onClick={() => onAnswer(answer)}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-150',
                'border focus:outline-none focus:ring-2 focus:ring-primary/30 focus:ring-offset-1',
                selected
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50'
              )}
            >
              {selected && <Check className="w-4 h-4" />}
              <span>{answer.label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  // Rendu principal selon le type
  const renderAnswers = () => {
    if (answerTypes.hasIcons) return renderIconAnswers();
    if (answerTypes.hasBoolean) return renderBooleanAnswers();
    if (answerTypes.hasBooleanKeys) return renderBooleanKeyAnswers();
    if (answerTypes.hasInputs) return renderInputAnswer();
    if (answerTypes.hasInputsWithUnit) return renderInputWithUnitAnswer();
    if (answerTypes.hasNumbers) return renderNumberAnswers();
    if (answerTypes.hasStrings) return renderStringAnswers();
    if (answerTypes.hasLabels) return renderLabelAnswers();
    return null;
  };

  // TODO: remove once the backend exposes a tooltip field on questions
  const questionTooltip: string | undefined =
    question.key === 'smoker'
      ? "Non-fumeur signifie que je ne fume pas ou que j'ai arrêté de fumer depuis plus de 24 mois."
      : undefined;

  return (
    <div className="px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-1.5 min-w-0 flex-shrink-0">
          <label className="text-sm text-slate-600">
            {question.label}
          </label>
          {questionTooltip && (
            <MessagesTooltip sections={[{ type: 'info', messages: [questionTooltip] }]}>
              <InfoIcon className="w-4 h-4 text-primary cursor-help" />
            </MessagesTooltip>
          )}
        </div>
        <div className="flex-1 flex justify-end max-w-xs">
          {renderAnswers()}
        </div>
      </div>
      {warningMessage && (
        <div className="mt-2 flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">{warningMessage}</p>
        </div>
      )}
    </div>
  );
};
